'use client';

import { useState, useCallback } from 'react';

type ContentActionsProps = {
  contentId: number;
  title: string;
  url: string;
};

const LIKED_KEY = 'enkini-liked';
const SAVED_KEY = 'enkini-saved';

function getSet(key: string): Set<string> {
  if (typeof window === 'undefined') return new Set();
  try {
    const raw = localStorage.getItem(key);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
}

function toggleSet(key: string, id: string): boolean {
  const set = getSet(key);
  const has = set.has(id);
  if (has) {
    set.delete(id);
  } else {
    set.add(id);
  }
  localStorage.setItem(key, JSON.stringify([...set]));
  return !has;
}

export default function ContentActions({ contentId, title, url }: ContentActionsProps) {
  const id = String(contentId);
  const [liked, setLiked] = useState(() => getSet(LIKED_KEY).has(id));
  const [saved, setSaved] = useState(() => getSet(SAVED_KEY).has(id));
  const [copied, setCopied] = useState(false);

  const handleLike = useCallback(() => {
    setLiked(toggleSet(LIKED_KEY, id));
  }, [id]);

  const handleSave = useCallback(() => {
    setSaved(toggleSet(SAVED_KEY, id));
  }, [id]);

  const handleShare = useCallback(async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title, url });
      } catch {
        // user cancelled
      }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch {
        // clipboard unavailable
      }
    }
  }, [title, url]);

  return (
    <div className="mt-10 flex items-center justify-center gap-3 border-y border-stone-100 py-6 sm:mt-12 sm:gap-4 sm:py-8">
      <button
        onClick={handleLike}
        aria-pressed={liked}
        aria-label={liked ? 'Beğeniyi geri al' : 'Beğen'}
        className={`inline-flex min-h-11 items-center gap-2 rounded-full border px-5 py-2.5 text-sm font-semibold shadow-sm transition-all duration-200 ${
          liked
            ? 'border-rose-200 bg-rose-50 text-rose-600'
            : 'border-stone-200 bg-white text-stone-700 hover:border-orange-300 hover:text-orange-600 hover:shadow-sm'
        }`}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M7 22V11M2 13v7a2 2 0 0 0 2 2h12.4a2 2 0 0 0 1.96-1.64l1.2-6A2 2 0 0 0 17.62 11H14V5a3 3 0 0 0-3-3L7 8.5" />
        </svg>
        {liked ? 'Beğenildi' : 'Beğen'}
      </button>

      <button
        onClick={handleSave}
        aria-pressed={saved}
        aria-label={saved ? 'Kaydı kaldır' : 'Kaydet'}
        className={`inline-flex min-h-11 items-center gap-2 rounded-full border px-5 py-2.5 text-sm font-semibold shadow-sm transition-all duration-200 ${
          saved
            ? 'border-amber-200 bg-amber-50 text-amber-700'
            : 'border-stone-200 bg-white text-stone-700 hover:border-orange-300 hover:text-orange-600 hover:shadow-sm'
        }`}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z" />
        </svg>
        {saved ? 'Kaydedildi' : 'Kaydet'}
      </button>

      <button
        onClick={handleShare}
        aria-label="Paylaş"
        className="inline-flex min-h-11 items-center gap-2 rounded-full border border-stone-200 bg-white px-5 py-2.5 text-sm font-semibold text-stone-700 shadow-sm transition-all duration-200 hover:border-orange-300 hover:text-orange-600 hover:shadow-sm"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" /><polyline points="16 6 12 2 8 6" /><line x1="12" x2="12" y1="2" y2="15" />
        </svg>
        {copied ? 'Bağlantı Kopyalandı' : 'Paylaş'}
      </button>
    </div>
  );
}
